<script setup>
/**
 * Envio de um modelo de WhatsApp a vários contactos de uma vez.
 *
 * Existe porque a equipa manda o mesmo modelo de abordagem dezenas de vezes
 * por dia, um contacto de cada vez.
 *
 * Cada variável do modelo pode ser preenchida de duas maneiras, porque os
 * modelos em uso precisam das duas: no de abordagem as variáveis são o nome
 * de quem envia, a especialidade e a região — iguais para todo o lote — e no
 * de reabrir conversa a variável é o nome da pessoa, diferente em cada
 * mensagem. Por omissão é igual para todos, que é o caso mais comum.
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import ContactAPI from 'dashboard/api/contacts';
import {
  buildTemplateParameters,
  replaceTemplateVariables,
  findComponentByType,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
} from 'dashboard/helper/templateHelper';
import { adivinharPrimeiroNome } from './primeiroNome';

const MAXIMO = 10;
const IGUAL = 'igual';
const POR_CONTACTO = 'porContacto';

const emit = defineEmits(['sent']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const caixasWhatsApp = useMapGetter('inboxes/getWhatsAppInboxes');
const utilizador = useMapGetter('getCurrentUser');

const contactos = ref([]);
const caixaId = ref(null);
const modelo = ref(null);
const modos = ref({}); // { '1': 'igual' | 'porContacto' }
const comuns = ref({}); // { '1': 'Talissa' }
const porContacto = ref({}); // { [contactoId]: { '1': 'Leticia' } }
const resultados = ref({}); // { [contactoId]: 'enviando' | 'ok' | mensagem }
const aEnviar = ref(false);
const terminado = ref(false);

const caixaEscolhida = computed(() =>
  caixasWhatsApp.value.find(i => i.id === caixaId.value)
);

const esqueleto = computed(() =>
  modelo.value ? buildTemplateParameters(modelo.value) : {}
);

/** As chaves do corpo do modelo, por ex. ['1','2','3','4']. */
const variaveis = computed(() => Object.keys(esqueleto.value.body || {}));

const temVariaveis = computed(() => variaveis.value.length > 0);

const temAlgumaPorContacto = computed(() =>
  variaveis.value.some(k => modos.value[k] === POR_CONTACTO)
);

const textoDoCorpo = computed(
  () => findComponentByType(modelo.value || {}, COMPONENT_TYPES.BODY)?.text || ''
);

/** Modelos com imagem ou ficheiro no cabeçalho precisam de um anexo por
 *  mensagem; isso não cabe num envio em lote, por isso ficam de fora. */
const temCabecalhoComMedia = computed(() =>
  MEDIA_FORMATS.includes(
    findComponentByType(modelo.value || {}, COMPONENT_TYPES.HEADER)?.format
  )
);

const etapa = computed(() => {
  if (!caixaId.value) return 'caixa';
  if (!modelo.value) return 'modelo';
  return 'variaveis';
});

const paramsDe = contacto => ({
  ...esqueleto.value,
  body: Object.fromEntries(
    variaveis.value.map(k => [
      k,
      modos.value[k] === POR_CONTACTO
        ? porContacto.value[contacto.id]?.[k] || ''
        : comuns.value[k] || '',
    ])
  ),
});

const tudoPreenchido = computed(() =>
  variaveis.value.every(k =>
    modos.value[k] === POR_CONTACTO
      ? contactos.value.every(c => (porContacto.value[c.id]?.[k] || '').trim())
      : (comuns.value[k] || '').trim()
  )
);

const previsualizacao = computed(() => {
  const primeiro = contactos.value[0];
  if (!modelo.value || !primeiro) return '';
  return replaceTemplateVariables(textoDoCorpo.value, paramsDe(primeiro));
});

const reiniciar = () => {
  contactos.value = [];
  caixaId.value =
    caixasWhatsApp.value.length === 1 ? caixasWhatsApp.value[0].id : null;
  modelo.value = null;
  modos.value = {};
  comuns.value = {};
  porContacto.value = {};
  resultados.value = {};
  aEnviar.value = false;
  terminado.value = false;
};

const open = lista => {
  reiniciar();
  contactos.value = lista.slice(0, MAXIMO);
  dialogRef.value?.open();
};

const escolherModelo = escolhido => {
  modelo.value = escolhido;
  const chaves = Object.keys(buildTemplateParameters(escolhido).body || {});
  modos.value = Object.fromEntries(chaves.map(k => [k, IGUAL]));
  comuns.value = Object.fromEntries(chaves.map(k => [k, '']));
  porContacto.value = Object.fromEntries(
    contactos.value.map(c => [c.id, Object.fromEntries(chaves.map(k => [k, '']))])
  );
};

const trocarModo = chave => {
  const novo = modos.value[chave] === IGUAL ? POR_CONTACTO : IGUAL;
  modos.value = { ...modos.value, [chave]: novo };
  if (novo !== POR_CONTACTO) return;
  // ao passar a "por contacto", adianta-se o primeiro nome de cada um
  porContacto.value = Object.fromEntries(
    contactos.value.map(c => [
      c.id,
      {
        ...porContacto.value[c.id],
        [chave]:
          porContacto.value[c.id]?.[chave] || adivinharPrimeiroNome(c.name),
      },
    ])
  );
};

const voltarAoModelo = () => {
  modelo.value = null;
};

const enviarAUm = async contacto => {
  const { data } = await ContactAPI.getContactableInboxes(contacto.id);
  const alvo = (data.payload || []).find(
    i => i.inbox?.id === caixaEscolhida.value.id
  );
  if (!alvo) throw new Error(t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.NO_SOURCE'));

  const params = paramsDe(contacto);

  await store.dispatch('contactConversations/create', {
    params: {
      inboxId: caixaEscolhida.value.id,
      sourceId: alvo.source_id,
      contactId: contacto.id,
      assigneeId: utilizador.value.id,
      message: {
        content: replaceTemplateVariables(textoDoCorpo.value, params),
        template_params: {
          name: modelo.value.name,
          category: modelo.value.category,
          language: modelo.value.language,
          processed_params: params,
        },
      },
    },
    isFromWhatsApp: true,
  });
};

const enviar = async () => {
  aEnviar.value = true;
  for (const contacto of contactos.value) {
    resultados.value = { ...resultados.value, [contacto.id]: 'enviando' };
    try {
      // sequencial de propósito: mantém a ordem e não atropela a API da Meta
      // eslint-disable-next-line no-await-in-loop
      await enviarAUm(contacto);
      resultados.value = { ...resultados.value, [contacto.id]: 'ok' };
    } catch (erro) {
      resultados.value = {
        ...resultados.value,
        [contacto.id]:
          erro?.response?.data?.message ||
          erro?.message ||
          t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.FAILED'),
      };
    }
  }
  aEnviar.value = false;
  terminado.value = true;
  emit('sent');
};

const contagemOk = computed(
  () => Object.values(resultados.value).filter(r => r === 'ok').length
);

const bloqueado = computed(() => aEnviar.value || terminado.value);

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.TITLE')"
    :description="
      t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.DESCRIPTION', contactos.length, {
        count: contactos.length,
      })
    "
    :show-confirm-button="false"
    :cancel-button-label="
      terminado
        ? t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.CLOSE')
        : t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.CANCEL')
    "
    overflow-y-auto
    width="2xl"
  >
    <div class="flex flex-col gap-4 max-h-[28rem] overflow-y-auto">
      <!-- 1. caixa de entrada -->
      <div v-if="etapa === 'caixa'" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.PICK_INBOX') }}
        </span>
        <button
          v-for="caixa in caixasWhatsApp"
          :key="caixa.id"
          class="flex items-center gap-2 px-3 py-2 text-sm text-left border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-1"
          @click="caixaId = caixa.id"
        >
          <span class="i-lucide-message-circle text-n-teal-10" />
          {{ caixa.name }}
        </button>
      </div>

      <!-- 2. modelo -->
      <div v-else-if="etapa === 'modelo'" class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.PICK_TEMPLATE') }}
          </span>
          <Button
            sm
            ghost
            slate
            :label="t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL')"
            @click="caixaId = null"
          />
        </div>
        <TemplatesPicker :inbox-id="caixaId" @on-select="escolherModelo" />
      </div>

      <!-- 3. variáveis e envio -->
      <div v-else class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ modelo.name }}
          </span>
          <Button
            v-if="!bloqueado"
            sm
            ghost
            slate
            :label="t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL')"
            @click="voltarAoModelo"
          />
        </div>

        <p
          v-if="temCabecalhoComMedia"
          class="px-3 py-2 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
        >
          {{ t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.MEDIA_UNSUPPORTED') }}
        </p>

        <!-- uma linha por variável -->
        <div v-if="temVariaveis" class="flex flex-col gap-2">
          <div
            v-for="chave in variaveis"
            :key="chave"
            class="flex items-center gap-3"
          >
            <span class="w-20 text-sm shrink-0 text-n-slate-11">
              {{
                t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.VARIABLE', {
                  number: chave,
                })
              }}
            </span>
            <Button
              sm
              ghost
              slate
              :disabled="bloqueado"
              :label="
                modos[chave] === 'porContacto'
                  ? t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.PER_CONTACT')
                  : t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.SAME_FOR_ALL')
              "
              class="shrink-0"
              @click="trocarModo(chave)"
            />
            <Input
              v-if="modos[chave] !== 'porContacto'"
              v-model="comuns[chave]"
              :disabled="bloqueado"
              class="flex-1"
            />
            <span v-else class="flex-1 text-xs text-n-slate-10">
              {{ t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.PER_CONTACT_HINT') }}
            </span>
          </div>
        </div>

        <p
          v-if="previsualizacao"
          class="px-3 py-2 text-sm whitespace-pre-line rounded-lg bg-n-alpha-1 text-n-slate-11"
        >
          {{ previsualizacao }}
        </p>

        <!-- a lista dos contactos -->
        <div class="flex flex-col gap-2">
          <div
            v-for="contacto in contactos"
            :key="contacto.id"
            class="flex items-center gap-3"
          >
            <div class="flex-1 min-w-0">
              <p class="text-sm truncate text-n-slate-12">{{ contacto.name }}</p>
              <p class="text-xs truncate text-n-slate-10">
                {{ contacto.phoneNumber }}
              </p>
            </div>
            <div v-if="temAlgumaPorContacto" class="flex gap-2 shrink-0">
              <template v-for="chave in variaveis" :key="chave">
                <Input
                  v-if="modos[chave] === 'porContacto'"
                  v-model="porContacto[contacto.id][chave]"
                  :disabled="bloqueado"
                  class="w-40"
                />
              </template>
            </div>
            <span class="w-24 text-xs text-right shrink-0">
              <span
                v-if="resultados[contacto.id] === 'ok'"
                class="text-n-teal-11"
              >
                {{ t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.SENT') }}
              </span>
              <span
                v-else-if="resultados[contacto.id] === 'enviando'"
                class="text-n-slate-10"
              >
                {{ t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.SENDING') }}
              </span>
              <span
                v-else-if="resultados[contacto.id]"
                v-tooltip.left="resultados[contacto.id]"
                class="text-n-ruby-11"
              >
                {{ t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.FAILED') }}
              </span>
            </span>
          </div>
        </div>

        <p v-if="terminado" class="text-sm text-n-slate-11">
          {{
            t('CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.SUMMARY', {
              ok: contagemOk,
              total: contactos.length,
            })
          }}
        </p>

        <Button
          v-if="!terminado"
          :label="
            t(
              'CONTACTS_BULK_ACTIONS.SEND_TEMPLATE.CONFIRM',
              contactos.length,
              { count: contactos.length }
            )
          "
          :disabled="!tudoPreenchido || aEnviar || temCabecalhoComMedia"
          :is-loading="aEnviar"
          class="self-end"
          @click="enviar"
        />
      </div>
    </div>
  </Dialog>
</template>
